import type { Questionnaire, QuestionnaireItem } from '@medplum/fhirtypes'
import { ExtendedQuestionnaire } from '@seen/core/models/questionnaire'
import { extractQuestionnaireItemsRecursive } from '@seen/core/questionnaire'
import {
  extractTextFromHTML,
  PageLayout,
  RichTextEditor,
  scrollIntoView,
} from '@seen/react'
import { isEmpty } from 'lodash-es'
import { type JSX, useRef, useState } from 'react'
import { match } from 'ts-pattern'

export function QuestionnaireToZodPage(): JSX.Element {
  const [input, setInput] = useState('')
  const [zodSchema, setZodSchema] = useState('')
  const pageRef = useRef<HTMLDivElement>(null)
  return (
    <PageLayout
      contentClassName="block space-y-6"
      contentRef={pageRef}
      title="Questionnaire to Zod"
    >
      <div className="flex gap-4">
        <RichTextEditor
          className="min-w-0 flex-1"
          defaultValue=""
          onChange={(value) => {
            if (value.length > input.length + 50) {
              scrollIntoView(pageRef.current) // Scroll to top of page. Prevents the browser from scrolling to the bottom of copy-pasted content.
            }
            setInput(value)
            let questionnaire
            try {
              questionnaire = JSON.parse(extractTextFromHTML(value))
            } catch {
              setZodSchema('Invalid JSON.')
              return
            }
            if (!ExtendedQuestionnaire.isInstance(questionnaire)) {
              setZodSchema(
                'JSON does not represent a valid Questionnaire resource.'
              )
              return
            }
            setZodSchema(questionnaireToZod(questionnaire))
          }}
          placeholder="Enter Questionnaire JSON…"
        />
        {!isEmpty(zodSchema) && (
          <RichTextEditor
            className="flex-1"
            key={zodSchema}
            defaultValue={`<pre><code>${zodSchema}</code></pre>`}
          />
        )}
      </div>
    </PageLayout>
  )
}

function questionnaireToZod(questionnaire: Questionnaire): string {
  const items = extractQuestionnaireItemsRecursive(questionnaire.item ?? [])
  return `z.object({
    ${items.map(itemToZod).join(',\n    ')}
})`
}

function itemToZod(item: QuestionnaireItem): string {
  return match(item.type)
    .with('boolean', () => `'${item.linkId}': z.boolean().nullable()`)
    .with('decimal', () => `'${item.linkId}': z.number().nullable()`)
    .with('integer', () => `'${item.linkId}': z.number().int().nullable()`)
    .with('date', () => `'${item.linkId}': z.string().date().nullable()`)
    .with(
      'dateTime',
      () => `'${item.linkId}': z.string().datetime().nullable()`
    )
    .with('time', () => `'${item.linkId}': z.string().time().nullable()`)
    .with('string', 'text', () => `'${item.linkId}': z.string().nullable()`)
    .with('url', () => `'${item.linkId}': z.string().url().nullable()`)
    .with(
      'choice',
      'open-choice',
      () =>
        `'${item.linkId}': z.enum([${item.answerOption?.map((option) => `'${option.valueCoding?.code}'`).join(', ')}]).nullable()`
    )
    .otherwise(() => `'${item.linkId}': z.unknown()`)
}
